import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { resolveAccountReliabilityDisplay } from '@/lib/campaigns/resolveAccountReliabilityDisplay';

type AccountReliability = {
  status?: string | null;
  score?: number | null;
  engagement_rate?: number | null;
  lifetime_views?: string | null;
  tracked_content?: number | null;
  username?: string | null;
} | null | undefined;

type AccountReliabilityBadgeProps = {
  reliability: AccountReliability;
  variant?: 'badge' | 'hero';
};

function formatLifetimeViews(value: string | null | undefined): string {
  if (value == null) return '—';

  try {
    return BigInt(value).toLocaleString();
  } catch {
    return value;
  }
}

export function AccountReliabilityBadge({
  reliability,
  variant = 'badge',
}: AccountReliabilityBadgeProps) {
  const { t } = useTranslation();
  const display = resolveAccountReliabilityDisplay(reliability);

  if (display.kind === 'blank') return null;

  const tooltip = t('admin.campaigns.submissions.reliability.tooltip', {
    username: reliability?.username || '—',
    rate:
      typeof reliability?.engagement_rate === 'number' &&
      Number.isFinite(reliability.engagement_rate)
        ? reliability.engagement_rate.toFixed(2)
        : '—',
    views: formatLifetimeViews(reliability?.lifetime_views),
    videos: reliability?.tracked_content ?? '—',
  });

  if (variant === 'hero') {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5',
          display.kind === 'warning' && 'text-amber-500',
        )}
        data-testid={
          display.kind === 'warning'
            ? 'account-reliability-warning'
            : 'account-reliability-score'
        }
        title={tooltip}
      >
        {display.kind === 'warning' && (
          <AlertTriangle className="h-5 w-5 shrink-0" />
        )}
        <span className="text-2xl font-semibold tabular-nums leading-none">
          {display.score}
        </span>
      </div>
    );
  }

  if (display.kind === 'warning') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-500 bg-amber-500/15 text-amber-500 text-xs font-medium"
        data-testid="account-reliability-warning"
        title={tooltip}
      >
        <AlertTriangle className="h-3 w-3" />
        {t('admin.campaigns.submissions.reliability.lowBadge', {
          score: display.score,
        })}
      </Badge>
    );
  }

  return (
    <span
      data-testid="account-reliability-score"
      className="inline-flex items-center rounded-md border border-border bg-muted/80 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-foreground"
      title={tooltip}
    >
      {display.score}
    </span>
  );
}
